import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { api, ApiError } from '../lib/api'
import { useSession } from '../lib/session'

/**
 * Getting back in (05 §5, WS4-09).
 *
 * This screen used to be a placeholder. It said *"Your rooms, photos and selections load here in
 * WS4-09"* — to homeowners, in production — and its other branch said "ask for a new one" with
 * nothing to ask with. Nothing in the PWA had ever called `POST /auth/magic-link`, so a person
 * whose cookie was gone had no route back into their own workspace at all.
 *
 * That is not an edge case, it is the normal path. Andrew's "Andrew has your list" email contains
 * a `/w/<id>` link; a homeowner opens it days later, from Mail, which hands it to whichever
 * browser is default — and a browser that never signed in has no session cookie. The workspace
 * returns 401 and, before this, said "open it again from the link Andrew emailed you", which is
 * exactly what they had just done.
 *
 * So: signed in, go where they were going. Not signed in, one field and one button.
 */
export function Resume() {
  const { identity, status } = useSession()
  const [params] = useSearchParams()
  const navigate = useNavigate()

  // Where they were headed before we asked who they were. Same-site paths only — this ends up
  // as a redirect, and `//evil.example` is a different origin to a browser.
  const nextRaw = params.get('next') ?? ''
  const next = nextRaw.startsWith('/') && !nextRaw.startsWith('//') ? nextRaw : ''

  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lookup, setLookup] = useState<'idle' | 'searching' | 'none'>('idle')

  const signedIn = identity?.principal === 'homeowner'

  // Signed in already: go straight there. If we were not told where, ask the API where they
  // left off — that is the whole cross-device resume.
  useEffect(() => {
    if (!signedIn) return
    if (next) {
      navigate(next, { replace: true })
      return
    }
    let cancelled = false
    setLookup('searching')
    api
      .get<{ session_id: string }>('/workspace/mine')
      .then((mine) => {
        if (!cancelled) navigate(`/w/${mine.session_id}`, { replace: true })
      })
      .catch(() => {
        if (!cancelled) setLookup('none')
      })
    return () => {
      cancelled = true
    }
  }, [signedIn, next, navigate])

  if (status === 'loading' || status === 'idle') {
    return <Shell>Opening your session…</Shell>
  }

  if (signedIn && lookup !== 'none') {
    return <Shell>Taking you back to your room…</Shell>
  }

  if (signedIn && lookup === 'none') {
    return (
      <Shell>
        <p className="eyebrow">Signed in</p>
        <h1 className="mt-4 text-3xl">Nothing in progress.</h1>
        <p className="mt-4 text-body">
          You do not have a design session open. Andrew sets one up after your visit is booked.
        </p>
        <a
          href="/estimate"
          className="mt-6 inline-block rounded-btn bg-blue px-7 py-3.5 font-bold text-white shadow-blue"
        >
          Get a free estimate
        </a>
      </Shell>
    )
  }

  if (sent) {
    return (
      <Shell>
        <p className="eyebrow">Check your inbox</p>
        <h1 className="mt-4 text-3xl">On its way.</h1>
        <p className="mt-4 text-body">
          If we have an estimate for <strong className="text-ink">{email}</strong>, there is a link
          in your inbox now. It works once and lasts 15 minutes.
        </p>
        <p className="mt-3 text-sm text-muted">
          Nothing after a minute? Check spam, then{' '}
          <button
            type="button"
            onClick={() => setSent(false)}
            className="font-semibold text-blue-deep underline underline-offset-2"
          >
            try a different address
          </button>
          .
        </p>
      </Shell>
    )
  }

  return (
    <Shell>
      <p className="eyebrow">Pick up where you left off</p>
      <h1 className="mt-4 text-3xl">We'll email you a link.</h1>
      <p className="mt-4 text-body">
        Use the address you gave Andrew. No password — the link signs you straight back into your
        room.
      </p>

      <form
        className="mt-6"
        onSubmit={async (event) => {
          event.preventDefault()
          setBusy(true)
          setError(null)
          try {
            await api.post('/auth/magic-link', {
              email,
              // Carried through the link, so the click lands them where they were going rather
              // than back on this page.
              redirect_path: next || undefined,
            })
            setSent(true)
          } catch (failure) {
            setError(
              failure instanceof ApiError
                ? (failure.problem.detail ?? failure.problem.title)
                : 'That did not send. Try again in a moment.',
            )
          } finally {
            setBusy(false)
          }
        }}
      >
        <label htmlFor="resume-email" className="block text-sm font-semibold text-ink">
          Your email
        </label>
        <input
          id="resume-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="you@example.com"
          className="mt-2 min-h-12 w-full rounded-btn border border-line bg-white px-4 text-base text-ink"
        />
        {error && (
          <p role="alert" className="mt-2 text-sm text-bad">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          className="mt-4 min-h-12 w-full rounded-btn bg-blue px-7 font-bold text-white shadow-blue disabled:opacity-50 sm:w-auto"
        >
          {busy ? 'Sending…' : 'Email me a link'}
        </button>
      </form>
    </Shell>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto max-w-2xl px-5 py-14">{children}</div>
}
